'use client'

import React from 'react'
import { LineChartWidget } from './widgets/LineChartWidget'
import { DataTableWidget } from './widgets/DataTableWidget'
import { InfoCardWidget } from './widgets/InfoCardWidget'
import { CodeBlockWidget } from './widgets/CodeBlockWidget'

// ============================================================================
// Types
// ============================================================================

interface UIComponentData {
  type: 'LineChart' | 'BarChart' | 'DataTable' | 'InfoCard' | 'CodeBlock'
  title?: string
  data: Record<string, unknown>
}

interface DynamicWidgetRendererProps {
  components: UIComponentData[]
}

// ============================================================================
// Dynamic Widget Renderer
// ============================================================================

export function DynamicWidgetRenderer({ components }: DynamicWidgetRendererProps) {
  if (!components || components.length === 0) {
    return null
  }

  return (
    <div className="space-y-4 mt-4">
      {components.map((comp, idx) => (
        <AdaptiveCard key={idx} component={comp} />
      ))}
    </div>
  )
}

// ============================================================================
// Adaptive Card - Routes to specific widget based on type
// ============================================================================

function AdaptiveCard({ component }: { component: UIComponentData }) {
  try {
    switch (component.type) {
      case 'LineChart':
      case 'BarChart':
        return <LineChartWidget data={component.data as never} title={component.title} type={component.type} />
      
      case 'DataTable':
        return <DataTableWidget data={component.data as never} title={component.title} />
      
      case 'InfoCard':
        return <InfoCardWidget data={component.data as never} />
      
      case 'CodeBlock':
        return <CodeBlockWidget data={component.data as never} title={component.title} />
      
      default:
        return (
          <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              Unsupported component type: <code className="font-mono">{component.type}</code>
            </p>
          </div>
        )
    }
  } catch (error) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-lg p-4">
        <p className="text-sm text-red-800">
          Error rendering component: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    )
  }
}
